import { Injectable } from '@angular/core';
import { Firestore, doc, getDoc } from 'firebase/firestore';

/** Id заведения в адресе — UUID; всё остальное в `/<сегмент>` — slug */
const STORE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Код стола — 7 символов Crockford Base32 (цифры и буквы без I, L, O, U) */
const TABLE_CODE = /^[0-9A-HJKMNP-TV-Z]{7}$/;

export function isStoreId(segment: string): boolean {
  return STORE_ID.test(segment);
}

/**
 * Код стола из адреса, в том числе набранного вручную: заглавными, без
 * пробелов и дефисов; похожие символы — как в Crockford Base32 (O → 0,
 * I и L → 1). Неверный код — null
 */
export function normalizeTableCode(raw: string): string | null {
  const code = decodeURIComponent(raw)
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1');
  return TABLE_CODE.test(code) ? code : null;
}

/** Стол по коду: заведение и qrCode стола (как в старом адресе) */
export interface TableLink {
  store: string;
  qrCode: string;
}

/**
 * Новые адреса меню (edim_app docs/ROADMAP.md, раздел 3a):
 * `edim.menu/T/<код>` — стол (tableCodes), `edim.menu/<slug>` — заведение
 * без стола (storeSlugs). Одно чтение документа, дальше меню грузится как
 * по старому адресу `/<заведение>/<qrCode>`
 */
@Injectable({ providedIn: 'root' })
export class LinkService {
  constructor(private store: Firestore) {}

  async resolveTableCode(raw: string): Promise<TableLink | null> {
    const code = normalizeTableCode(raw);
    if (!code) return null;
    try {
      const snapshot = await getDoc(doc(this.store, 'tableCodes', code));
      const data = snapshot.data();
      return data && typeof data['store'] === 'string' && typeof data['qrCode'] === 'string'
        ? { store: data['store'], qrCode: data['qrCode'] }
        : null;
    } catch (error) {
      console.warn('LinkService | resolveTableCode() |', code, error);
      return null;
    }
  }

  /** Заведение по slug; null — не найдено */
  async resolveSlug(raw: string): Promise<string | null> {
    const slug = decodeURIComponent(raw).trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/.test(slug)) return null;
    try {
      const store = (await getDoc(doc(this.store, 'storeSlugs', slug))).data()?.['store'];
      return typeof store === 'string' ? store : null;
    } catch (error) {
      console.warn('LinkService | resolveSlug() |', slug, error);
      return null;
    }
  }
}
